import React, { useCallback, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import '@aws-amplify/ui-react/styles.css';
import { Box, CircularProgress, Toolbar } from '@mui/material';

import { snackbarQueueAtom } from 'atoms/snackbarAtom';
import { SnackbarStack } from 'components/SnackbarStack';
import { errorAtom } from 'atoms/error';
import { withAuthenticator, WithAuthenticatorProps } from '@aws-amplify/ui-react';

import { useFetchAuthSession } from 'api/fetchAuthSession';
import { fetchMapping } from 'api/fetchCommon';
import { authSessionLoadingAtom, authTokenAtom } from 'atoms/authAtoms';
import { mappingAtom } from 'atoms/mappingAtoms';
import { powertrainAtom } from 'atoms/powertrainAtom';
import { copyToConfigAtom } from 'atoms/Rtool/copyToConfigAtoms';

import { AppHeader } from './subcomponents/AppHeader';
import { SideNav } from './subcomponents/SideNav';
import { RouteFade } from 'components/transitions/RouteFade';
import { WorkflowStepper } from 'components/WorkflowStepper';
import { CommandPalette } from 'components/CommandPalette';
import { ShortcutCheatsheet } from 'components/ShortcutCheatsheet';
import { OnboardingTour } from 'components/onboarding/OnboardingTour';
import { useGlobalShortcuts } from 'utils/hooks/useGlobalShortcuts';
import { useAutoCollapseSideNav } from 'utils/hooks/useAutoCollapseSideNav';
import { useNotifyCopyJobTerminalEvents, useCopyJobTitleIndicator } from 'utils/hooks/useCopyJobBlocking';

// R-Tool: MTG Vibration Pages
import { NewProject as RtoolNewProject } from 'pages/RtoolPage/NewProject';
import { ExistingProjects as RtoolExistingProjects } from 'pages/RtoolPage/ExistingProjects';
import { DataRegistration as RtoolDataRegistration } from 'pages/RtoolPage/DataRegistration';
import { Calculation as RtoolCalculation } from 'pages/RtoolPage/Calculation';
import { CabinGraphViewer as RtoolCabinGraphViewer } from 'pages/RtoolPage/CabinNoise';
import { MtgGraphViewer as RtoolMtgGraphViewer } from 'pages/RtoolPage/MtgVib';

// L-Tool Pages
import { NewProject as LtoolNewProject } from 'pages/LtoolPage/NewProject';
import { ExistingProjects as LtoolExistingProjects } from 'pages/LtoolPage/ExistingProjects';
import { DataRegistration as LtoolDataRegistration } from 'pages/LtoolPage/DataRegistration';
import { Calculation as LtoolCalculation } from 'pages/LtoolPage/Calculation';
import { MatrixViewer as LtoolMatrixViewer } from 'pages/LtoolPage/Matrix';

// Fallback
import NotFoundPage from 'pages/NotFoundPage';

const MainLayout: React.FC = () => {
    const [loading] = useAtom(authSessionLoadingAtom);
    const [navWidth, setNavWidth] = useState<number>(260);
    const authToken = useAtomValue(authTokenAtom);
    const setMapping = useSetAtom(mappingAtom);
    const [snackbarQueue, setSnackbarQueue] = useAtom(snackbarQueueAtom);
    const removeSnackbar = useCallback(
        (id: number) => setSnackbarQueue((q) => q.filter((s) => s.id !== id)),
        [setSnackbarQueue],
    );
    const { pathname } = useLocation();
    const setError = useSetAtom(errorAtom);
    const navigate = useNavigate();
    const setPowertrain = useSetAtom(powertrainAtom);
    const setCopyToConfig = useSetAtom(copyToConfigAtom);

    useFetchAuthSession();

    // グローバルキーボードショートカット（⌘K 含む）と狭幅時の SideNav 自動畳みを有効化する。
    useGlobalShortcuts();
    useAutoCollapseSideNav();
    // 丸ごとコピー非同期化（issue #43）: 画面遷移に依存せず、バックグラウンドでの終端到達を通知する。
    // issue #45: 通知の「結果を見る」action押下でコピー先画面へ遷移する（CopyJobIndicatorの
    // goToDestinationと同じパターン）。useNavigateはこのファイルが既に画面遷移を扱うため
    // ここに置き、useCopyJobBlocking.ts側はreact-router-domに依存させない
    // （そちらに置くとimportするだけでreact-router-dom v7の解決が必要になり、それを
    // importしていない既存の呼び出し元コンポーネントのテストが軒並み壊れるため）。
    useNotifyCopyJobTerminalEvents((entry) => {
        setPowertrain(entry.powertrain);
        setCopyToConfig(entry.config);
        navigate('/r-tools/data-registration');
    });
    // issue #44 §2.4: 別タブから見て分かるよう、document.titleに進行中マークを付ける。
    useCopyJobTitleIndicator();

    useEffect(() => {
        setError(null);
    }, [pathname, setError]);

    useEffect(() => {
        if (!authToken) return;
        fetchMapping(authToken)
            .then(setMapping)
            .catch((e) => console.error('[mappingAtom] fetchMapping failed:', e));
    }, [authToken, setMapping]);

    return (
        <Box sx={{ display: 'flex' }}>
            <AppHeader />
            <Box sx={{ flex: 1, display: 'flex' }}>
                <SideNav width={navWidth} onWidthChange={setNavWidth} />

                <Box component="main" sx={{ flexGrow: 1, overflow: 'auto', p: { xs: 2.5, sm: 3, md: 4, xl: 6 } }}>
                    <Toolbar />
                    <WorkflowStepper />
                    {loading ? (
                        <CircularProgress />
                    ) : (
                        <RouteFade>
                        <Routes>
                            {/* Redirect root to default R-Tool view */}
                            <Route path="/" element={<Navigate to="/r-tools/new-project" replace />} />

                            {/* R-Tools section */}
                            <Route path="r-tools" element={<Outlet />}>
                                <Route index element={<Navigate to="new-project" replace />} />
                                <Route path="new-project" element={<RtoolNewProject />} />
                                <Route path="data-registration" element={<RtoolDataRegistration />} />
                                <Route path="existing-project" element={<RtoolExistingProjects />} />
                                <Route path="calculation" element={<RtoolCalculation />} />
                                <Route path="cabin-noise" element={<RtoolCabinGraphViewer />} />
                                <Route path="mtg-vib" element={<RtoolMtgGraphViewer />} />
                            </Route>

                            {/* L-Tools section */}
                            <Route path="l-tools" element={<Outlet />}>
                                <Route index element={<Navigate to="new-project" replace />} />
                                <Route path="new-project" element={<LtoolNewProject />} />
                                <Route path="data-registration" element={<LtoolDataRegistration />} />
                                <Route path="existing-project" element={<LtoolExistingProjects />} />
                                <Route path="calculation" element={<LtoolCalculation />} />
                                <Route path="matrix" element={<LtoolMatrixViewer />} />
                            </Route>

                            {/* Catch-all */}
                            <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                        </RouteFade>
                    )}
                </Box>
            </Box>
            <CommandPalette />
            <ShortcutCheatsheet />
            <OnboardingTour />
            <SnackbarStack items={snackbarQueue} onClose={removeSnackbar} />
        </Box >
    );
};

const App: React.FC<WithAuthenticatorProps> = () => (
    <Router>
        <MainLayout />
    </Router>
);

// E2E（BE なし）専用: REACT_APP_E2E_BYPASS_AUTH=true のビルドでは認証(withAuthenticator)を
// バイパスしてアプリを直接描画する。本番ビルドではフラグ未設定のため従来通り認証を要求する。
export default process.env.REACT_APP_E2E_BYPASS_AUTH === 'true'
    ? App
    : withAuthenticator(App, { hideSignUp: true });
